import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import { Link } from "../ui/Link/Link";
import { ThemeToggle } from "../ui/ThemeToggle/ThemeToggle";
import { StyleToggle } from "../ui/StyleToggle/StyleToggle";
import { LanguageToggle } from "../ui/LanguageToggle/LanguageToggle";
import { NotificationBell } from "../ui/NotificationBell/NotificationBell";
import { SupportWidget } from "../SupportWidget/SupportWidget";
import { useAuth } from "../../contexts/AuthContext";
import { MenuIcon } from "./sidebarIcons";

const Bar = styled.header`
  position: sticky;
  top: 0;
  z-index: 40;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  height: 3.75rem;
  padding: 0 1.25rem;
  background-color: ${({ theme }) =>
    theme.mode === "dark" ? "rgba(10,10,10,0.72)" : "rgba(255,255,255,0.72)"};
  backdrop-filter: blur(12px);
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const Wordmark = styled.span`
  font-size: 1.0625rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  background: ${({ theme }) => theme.colors.gradient};
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  white-space: nowrap;
`;

const Spacer = styled.div`
  flex: 1;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
`;

const Divider = styled.span`
  width: 1px;
  height: 1.35rem;
  margin: 0 0.35rem;
  background-color: ${({ theme }) => theme.colors.border};
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.1rem;
  height: 2.1rem;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.colors.background};
    color: ${({ theme }) => theme.colors.text};
  }
`;

const BurgerButton = styled(IconButton)`
  display: none;

  @media (max-width: 64rem) {
    display: inline-flex;
  }
`;

const Avatar = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.1rem;
  height: 2.1rem;
  border: none;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.gradient};
  color: ${({ theme }) => theme.colors.primaryForeground};
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
`;

const Menu = styled.div`
  position: absolute;
  top: 3.1rem;
  right: 0;
  min-width: 12rem;
  padding: 0.35rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background-color: ${({ theme }) => theme.colors.surface};
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.2);
  z-index: 50;

  .who {
    padding: 0.5rem 0.6rem 0.6rem;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    margin-bottom: 0.3rem;

    strong {
      display: block;
      font-size: 0.8125rem;
      color: ${({ theme }) => theme.colors.text};
    }
    span {
      font-size: 0.72rem;
      color: ${({ theme }) => theme.colors.textMuted};
    }
  }

  a,
  button {
    display: block;
    width: 100%;
    padding: 0.5rem 0.6rem;
    border: none;
    border-radius: ${({ theme }) => theme.borderRadius.md};
    background: transparent;
    color: ${({ theme }) => theme.colors.text};
    font-size: 0.8125rem;
    text-align: left;
    text-decoration: none;
    cursor: pointer;

    &:hover {
      background-color: ${({ theme }) => theme.colors.background};
    }
  }

  button.danger:hover {
    color: ${({ theme }) => theme.colors.danger};
  }
`;

const MenuAnchor = styled.div`
  position: relative;
`;

const initials = (name?: string) =>
  (name ?? "?")
    .split(/[\s._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "?";

/**
 * The slim top bar left after navigation moved into the sidebar.
 *
 * <p>Deliberately holds nothing that answers "where do I go". What remains acts on the current
 * session instead: appearance, language, support, notifications, and the account menu. That split
 * is what stopped the bar overflowing.
 */
export const AppHeader = ({
  onOpenMobileNav,
}: {
  onOpenMobileNav: () => void;
}) => {
  const { t } = useTranslation(["nav", "common"]);
  const { user, logout } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => setMenuOpen(false), [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (event: MouseEvent) => {
      if (!anchorRef.current?.contains(event.target as Node))
        setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [menuOpen]);

  return (
    <Bar>
      <BurgerButton
        type="button"
        onClick={onOpenMobileNav}
        aria-label={t("nav:open_menu", "Open menu")}
      >
        <MenuIcon />
      </BurgerButton>

      <Link to="/dashboard">
        <Wordmark>Kemblo</Wordmark>
      </Link>

      <Spacer />

      <Controls>
        <LanguageToggle />
        <StyleToggle />
        <ThemeToggle />

        <Divider />

        <NotificationBell />
        <SupportWidget />

        <MenuAnchor ref={anchorRef}>
          <Avatar
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-expanded={menuOpen}
            aria-label={t("nav:account", "Account")}
            title={user?.username}
          >
            {initials(user?.username)}
          </Avatar>

          {menuOpen && (
            <Menu>
              <div className="who">
                <strong>{user?.username}</strong>
                <span>{user?.email}</span>
              </div>

              {user?.role === "SUPER_ADMIN" && (
                <Link to="/admin/teams">
                  {t("nav:admin_panel", "Admin Panel")}
                </Link>
              )}

              <Link to="/account">{t("nav:account", "Account")}</Link>

              <button type="button" className="danger" onClick={logout}>
                {t("nav:logout", "Log out")}
              </button>
            </Menu>
          )}
        </MenuAnchor>
      </Controls>
    </Bar>
  );
};
